import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { get, set, isNative } from './sqlite-store';
import { rawRequest } from '../../lib/api';
import { apiUrl } from '../../lib/api-base';
import { genId } from '../../lib/uuid';
import { useUIStore } from '../../stores/ui-store';
import { offlineLog } from './log-buffer';
import { offlineToast } from '../../stores/offline-toast-store';

const MEDIA_QUEUE_KEY = 'tc-media-queue';
const MEDIA_DIR = 'dwf-media';

type MediaStatus = 'pending' | 'in-flight' | 'succeeded' | 'failed';

export interface QueuedMedia {
  id: string;
  entityType: string;
  entityId: string;
  name: string;
  mimeType: string;
  size: number;
  /** Filesystem path relative to app data directory */
  filePath: string;
  createdAt: number;
  attemptCount: number;
  status: MediaStatus;
  uploadedUrl?: string;
  error?: string;
}

/**
 * Bandwidth-aware media upload queue.
 * - Photos are compressed when on cellular (saveData / 2g / 3g).
 * - Full quality is kept on WiFi.
 * - Blobs stored on native filesystem via @capacitor/filesystem.
 * - Queue metadata stored in SQLite via sqlite-store.
 */

function getConnection(): {
  effectiveType?: string;
  saveData?: boolean;
  type?: string;
} | null {
  if (typeof navigator === 'undefined') return null;
  const conn = (navigator as unknown as {
    connection?: { effectiveType?: string; saveData?: boolean; type?: string };
  }).connection;
  return conn ?? null;
}

function shouldCompress(): boolean {
  const conn = getConnection();
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.type === 'cellular') return true;
  if (conn.effectiveType && ['slow-2g', '2g', '3g'].includes(conn.effectiveType))
    return true;
  return false;
}

async function compressImage(
  blob: Blob,
  maxDimension = 1600,
  quality = 0.75,
): Promise<Blob> {
  if (!blob.type.startsWith('image/')) return blob;
  const bitmap = await createImageBitmap(blob).catch(() => null);
  if (!bitmap) return blob;

  let { width, height } = bitmap;
  if (width <= maxDimension && height <= maxDimension) return blob;
  const scale = maxDimension / Math.max(width, height);
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return blob;
  ctx.drawImage(bitmap, 0, 0, width, height);

  return new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (b) => resolve(b || blob),
      'image/jpeg',
      quality,
    );
  });
}

/** Convert a Blob to a base64 data string for Filesystem.writeFile */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the data:... prefix
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Write a blob to the native filesystem. Returns the relative path. */
async function saveBlob(id: string, blob: Blob): Promise<string> {
  const ext = blob.type.split('/')[1] || 'bin';
  const filePath = `${MEDIA_DIR}/${id}.${ext}`;
  const base64Data = await blobToBase64(blob);
  await Filesystem.writeFile({
    path: filePath,
    data: base64Data,
    directory: Directory.Data,
    recursive: true,
  });
  return filePath;
}

/** Read a blob back from the native filesystem. */
async function loadBlob(filePath: string): Promise<Blob | null> {
  try {
    const result = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Data,
    });
    // result.data is base64 string or Blob depending on platform
    if (result.data instanceof Blob) return result.data;
    const binary = atob(result.data as string);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes]);
  } catch {
    return null;
  }
}

/** Delete a blob from the native filesystem. */
async function deleteBlob(filePath: string): Promise<void> {
  try {
    await Filesystem.deleteFile({ path: filePath, directory: Directory.Data });
  } catch {
    // File may not exist — ignore
  }
}

class MediaQueue {
  private subscribers = new Set<() => void>();

  async load(): Promise<QueuedMedia[]> {
    return (await get<QueuedMedia[]>(MEDIA_QUEUE_KEY)) ?? [];
  }

  /** Queue a new media upload; blob is compressed if on cellular. */
  async enqueue(input: {
    entityType: string;
    entityId: string;
    file: File;
  }): Promise<QueuedMedia> {
    const blob = shouldCompress()
      ? await compressImage(input.file)
      : input.file;

    const id = genId();

    // On native: write to filesystem; on web: fall through (online direct upload)
    let filePath = '';
    if (isNative()) {
      filePath = await saveBlob(id, blob);
    }

    const entry: QueuedMedia = {
      id,
      entityType: input.entityType,
      entityId: input.entityId,
      name: input.file.name,
      mimeType: blob.type || input.file.type,
      size: blob.size,
      filePath,
      createdAt: Date.now(),
      attemptCount: 0,
      status: 'pending',
    };
    const queue = await this.load();
    queue.push(entry);
    await set(MEDIA_QUEUE_KEY, queue);
    offlineLog.sync(`Enqueued media ${id}: ${input.file.name} (${blob.size} bytes)`);
    this.emit();
    // Notify user
    offlineToast.queued(
      `Photo queued: ${input.file.name}`,
      'Will upload automatically when back online',
    );
    return entry;
  }

  async update(id: string, patch: Partial<QueuedMedia>): Promise<void> {
    const queue = await this.load();
    const idx = queue.findIndex((m) => m.id === id);
    if (idx < 0) return;
    queue[idx] = { ...queue[idx], ...patch };
    await set(MEDIA_QUEUE_KEY, queue);
    this.emit();
  }

  async remove(id: string): Promise<void> {
    const queue = await this.load();
    const item = queue.find((m) => m.id === id);
    if (item?.filePath) {
      await deleteBlob(item.filePath);
    }
    await set(
      MEDIA_QUEUE_KEY,
      queue.filter((m) => m.id !== id),
    );
    this.emit();
  }

  async pendingCount(): Promise<number> {
    const queue = await this.load();
    return queue.filter((m) => m.status === 'pending' || m.status === 'in-flight')
      .length;
  }

  subscribe(cb: () => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  private emit() {
    this.subscribers.forEach((cb) => cb());
  }

  /**
   * Upload all pending items. Called when connectivity returns.
   */
  async flush(): Promise<void> {
    const queue = await this.load();
    const pending = queue
      .filter((m) => m.status === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt);
    for (const item of pending) {
      await this.uploadOne(item);
    }
    const count = await this.pendingCount();
    useUIStore.getState().setPendingSyncCount(count);
  }

  private async uploadOne(item: QueuedMedia): Promise<void> {
    await this.update(item.id, {
      status: 'in-flight',
      attemptCount: item.attemptCount + 1,
    });

    try {
      let blob: Blob | null = null;

      if (isNative() && item.filePath) {
        blob = await loadBlob(item.filePath);
      }

      if (!blob) throw new Error('Blob missing from storage');

      // Upload endpoint expects multipart/form-data on /api/uploads
      const form = new FormData();
      form.append('file', new File([blob], item.name, { type: item.mimeType }));
      const token = localStorage.getItem('tc-auth-token');
      const res = await fetch(apiUrl('/uploads'), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const uploaded = (await res.json()) as {
        url: string;
        name: string;
        mimeType: string;
        size: number;
      };

      // Now create the attachment entity so the WO sees it
      await rawRequest('/entities/attachment', {
        method: 'POST',
        body: JSON.stringify({
          entityType: item.entityType,
          entityId: item.entityId,
          url: uploaded.url,
          name: uploaded.name,
          mimeType: uploaded.mimeType,
          size: uploaded.size,
          capturedAt: new Date(item.createdAt).toISOString(),
        }),
      });

      await this.update(item.id, {
        status: 'succeeded',
        uploadedUrl: uploaded.url,
      });
      offlineLog.sync(`Uploaded media ${item.id}: ${item.name}`);
      setTimeout(() => this.remove(item.id).catch(() => undefined), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      offlineLog.error(`Media upload failed for ${item.id}: ${message}`);
      await this.update(item.id, {
        status: 'pending',
        error: message,
      });
    }
  }
}

export const mediaQueue = new MediaQueue();

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    mediaQueue.flush().catch(() => undefined);
  });
  // Periodic retry
  setInterval(() => {
    if (navigator.onLine) mediaQueue.flush().catch(() => undefined);
  }, 30_000);
}

/**
 * Resolve a mediaRef:// URI to a local file URI for inline preview.
 * Returns null if the file doesn't exist or we're on web.
 */
export async function resolveLocalMedia(mediaRef: string): Promise<string | null> {
  if (!isNative()) return null;
  if (!mediaRef.startsWith('mediaRef://')) return null;
  const id = mediaRef.slice('mediaRef://'.length);
  const queue = await mediaQueue.load();
  const item = queue.find((m) => m.id === id);
  if (!item?.filePath) return null;
  try {
    const result = await Filesystem.getUri({
      path: item.filePath,
      directory: Directory.Data,
    });
    return Capacitor.convertFileSrc(result.uri);
  } catch {
    return null;
  }
}

/**
 * Get the total size of all files in the media directory.
 * Returns 0 on web or if directory doesn't exist.
 */
export async function getMediaStorageBytes(): Promise<number> {
  if (!isNative()) return 0;
  try {
    const result = await Filesystem.readdir({
      path: MEDIA_DIR,
      directory: Directory.Data,
    });
    let total = 0;
    for (const file of result.files) {
      total += file.size ?? 0;
    }
    return total;
  } catch {
    return 0;
  }
}

/** Delete the entire media directory (used on logout). */
export async function clearMediaStorage(): Promise<void> {
  if (!isNative()) return;
  try {
    await Filesystem.rmdir({
      path: MEDIA_DIR,
      directory: Directory.Data,
      recursive: true,
    });
  } catch {
    // Directory may not exist — ignore
  }
}
